import assetsUploadApiClient, { AssetType, FieldMask } from '@modules/clients/assetsupload';
import { useMutation } from '@tanstack/react-query';
import { PreviewFromJSON, Status } from '@rbx/clients/assetsUploadApi';
import { captureException } from '@sentry/nextjs';
// eslint-disable-next-line no-restricted-imports -- needed
import usePollOperationForAssetUploadMutation from '../../creations/placeThumbnails/hooks/usePollOperationForAssetUploadMutation';

class UploadOperationError extends Error {
  public status?: Status = undefined;

  constructor(message: string, status?: Status) {
    super(message);
    this.status = status;
  }
}

/**
 * Two steps to upload a thumbnail to a place:
 * 1. upload a thumbnail to the assets system,
 * 2. "associate" the newly uploaded thumbnail to the place along with the other thumbnails that the place already has
 */
const useUploadAssetForPlaceMutation = (onSuccess?: () => void) => {
  const { pollForCompletedOperationAsync: pollForUploadOperationAsync } =
    usePollOperationForAssetUploadMutation();
  const { pollForCompletedOperationAsync: pollForUpdateOperationAync } =
    usePollOperationForAssetUploadMutation();

  const { mutate: uploadAssetForPlace, mutateAsync: uploadAssetForPlaceAsync } = useMutation({
    mutationFn: async ({
      placeId,
      userId,
      groupId,
      isGroupUpload,
      file,
      assetType,
      price,
    }: {
      placeId: number;
      userId: number;
      groupId?: number;
      isGroupUpload?: boolean;
      file: File;
      assetType: AssetType;
      price: number;
    }) => {
      /** beginning of step 1. upload a thumbnail to the assets system, */
      const uploadRequestInfo = {
        assetType,
        displayName: `Asset Thumbnail`,
        creationContext: {
          creator: isGroupUpload ? { groupId } : { userId },
          expectedPrice: price,
        },
      };
      const uploadOperationId = await assetsUploadApiClient.createAssetAndGetOperationId(
        uploadRequestInfo,
        file,
        true, // setAssetPrivacyToOpenUse, required for newly uploaded Previews and Icons
      );
      const uploadOperation = await pollForUploadOperationAsync(uploadOperationId);
      if (!uploadOperation.response) {
        throw new UploadOperationError('Upload operation failed', uploadOperation.error);
      }
      const asset = uploadOperation.response;
      /** end of step 1 */

      /** beginning of step 2. "associate" the newly uploaded thumbnail to the place */
      const newPreviewItem = PreviewFromJSON({
        asset: `assets/${asset.assetId}`,
        altText: '',
      });
      const currentPreviewListResponse = await assetsUploadApiClient.getAsset(placeId, [
        FieldMask.PREVIEWS,
      ]);
      const currentPreviewList = currentPreviewListResponse.previews ?? [];
      const newPreviewList = [newPreviewItem, ...currentPreviewList];

      const updateOperationId = await assetsUploadApiClient.updateAssetAndGetOperationId(
        placeId,
        [FieldMask.PREVIEWS],
        {
          assetId: placeId,
          previews: newPreviewList,
        },
      );
      const updateOperation = await pollForUpdateOperationAync(updateOperationId);
      if (!updateOperation.response) {
        throw new UploadOperationError(
          'Associate uploaded asset to place operation failed',
          updateOperation.error,
        );
      }
      /** end of step 2 */

      return updateOperation.response;
    },
    retry: true,
    onSuccess,
    onError: (error) => {
      const errorObj =
        error instanceof Error
          ? error
          : new Error(
              'CreateYourPlace - Error occurred while uploading initial autogenerated thumbnail for Your Place',
            );
      captureException(errorObj);
    },
  });

  return { uploadAssetForPlace, uploadAssetForPlaceAsync };
};

export default useUploadAssetForPlaceMutation;
